import Card from 'antd/lib/card';
import Empty from 'antd/lib/empty';
import Typography from 'antd/lib/typography';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, LineChart } from 'echarts/charts';
import { DataZoomComponent, GridComponent, LegendScrollComponent, MarkAreaComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useMemo } from 'react';
import { EnergyUsageTimeseriesDto } from '../../../../domain/energy/interface';
import { SECONDARY_COLOR, PRIMARY_COLOR } from '../../../../theme';
import { IDateRange } from '../../../interface';
import { formatEnergyValueToFixed } from 'src/domain/common/formattersToAllowedValueLength';
import { groupEnergyUsageByHour } from './groupEnergyUsageByHour';
import { mean } from 'lodash';
import { renderTooltip } from './chartUtils';

echarts.use([BarChart, LineChart, TooltipComponent, GridComponent, CanvasRenderer, MarkAreaComponent, DataZoomComponent, LegendScrollComponent]);

interface IEnergyConsumptionWidgetProps {
    dateRange: IDateRange;
    energyData: EnergyUsageTimeseriesDto; // aggregate
}

export const EnergyConsumptionWidget = ({ dateRange, energyData }: IEnergyConsumptionWidgetProps) => {
    const isDataExist = Boolean(energyData?.values?.length);

    const preparedEnergyData = useMemo(() => {
        const values = prepareEnergyDataForBarChart(dateRange, energyData);

        return values.map(value => {
            return { value, itemStyle: { color: PRIMARY_COLOR } };
        });
    }, [energyData, dateRange]);

    const option = {
        useUTC: true,
        legend: {},
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'line',
                snap: true,
                lineStyle: {
                    type: 'solid',
                },
            },
            formatter: (params: any) => {
                return renderTooltip(params);
            },
        },
        grid: {
            bottom: '7%',
            right: '10%',
        },
        xAxis: {
            type: 'time',
            max: dateRange.end.valueOf(),
            min: dateRange.start.valueOf(),
            axisLabel: {
                color: '#697475',
                margin: 10,
                formatter: {
                    year: '{yyyy}',
                    month: '{MMM}',
                    day: '{dd}-{MMM}',
                    hour: '{HH}:{mm}',
                    minute: '{HH}:{mm}',
                },
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#d6dbdb',
                    width: 2,
                    type: 'solid',
                },
            },
            axisTick: {
                show: true,
            },
        },
        yAxis: {
            max: (value: any) => {
                return Math.round(value.max * 1.1);
            },
            axisLabel: {
                show: true,
            },
            name: 'kWh',
            nameLocation: 'middle',
            nameGap: 40,
        },
        series: [
            {
                type: 'bar',
                showSymbol: false,
                emphasis: {
                    focus: 'series',
                },
                name: 'Energy Consumption',
                data: preparedEnergyData,
                itemStyle: {
                    color: PRIMARY_COLOR,
                },
            },
        ],
        dataZoom: [
            {
                filterMode: 'weakFilter',
                type: 'inside',
                minSpan: 2,
            },
        ],
    };

    return (
        <Card className="site-energy-usage-widget" style={{ marginBottom: '16px' }}>
            <Typography.Title level={4} style={{ marginTop: 0, color: SECONDARY_COLOR }}>
                Energy Consumption Over Time&nbsp;(kWh)
            </Typography.Title>
            <div className="site-energy-chart-title">
                {isDataExist ? (
                    <ReactEChartsCore echarts={echarts} option={option} notMerge lazyUpdate />
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </div>
        </Card>
    );
};

function prepareEnergyDataForBarChart(dateRange: IDateRange, energyData: EnergyUsageTimeseriesDto): [number, number][] {
    const result: [number, number][] = [];
    if (!energyData?.values.length) return result;

    const hourMap = groupEnergyUsageByHour(dateRange, energyData);
    if (!hourMap) return result;

    hourMap.forEach((item, timestamp) => {
        const avg = mean(item.usageKw);
        const usageKwh = formatEnergyValueToFixed(avg, 1);
        result.push([timestamp, usageKwh]);
    });

    return result;
}
